import { collection, orderBy, query, where } from 'firebase/firestore';
import {
  ACTIVE_CHAT_STATUSES,
  CLOSED_CHAT_STATUSES,
  CHAT_MARK_AS_OPTIONS,
  chatStatusLabel,
  isActiveChatStatus,
  isClosedChatStatus,
  wasClosedByAgent,
} from './chatSessionStatus';

export {
  ACTIVE_CHAT_STATUSES,
  CLOSED_CHAT_STATUSES,
  CHAT_MARK_AS_OPTIONS,
  chatStatusLabel,
  isActiveChatStatus,
  isClosedChatStatus,
  wasClosedByAgent,
};

/**
 * Canonical chat session schema — keep in sync with functions/chatSchema.js.
 */
export const CHAT_SESSIONS_COLLECTION = 'chats';

/** Must match functions/chatSchema.js DEFAULT_SESSION_STATUS */
export const DEFAULT_SESSION_STATUS = 'open';

/** Legacy alias — new sessions start as open/active. */
export const OPEN_CHAT_STATUSES = ['open', 'active'];

/**
 * @param {string | null | undefined} companyId
 * @returns {string}
 */
export function resolveTenantCompanyId(companyId) {
  return typeof companyId === 'string' ? companyId.trim() : '';
}

/**
 * Tenant-scoped query for active chat sessions.
 */
export function buildOpenChatSessionsQuery(firestore, companyId) {
  const tenantCompanyId = resolveTenantCompanyId(companyId);
  if (!tenantCompanyId) {
    throw new Error('buildOpenChatSessionsQuery: companyId is required');
  }

  return query(
    collection(firestore, CHAT_SESSIONS_COLLECTION),
    where('companyId', '==', tenantCompanyId),
    where('status', 'in', ACTIVE_CHAT_STATUSES),
    orderBy('updatedAt', 'desc'),
  );
}

/**
 * Tenant-scoped query for archived chat sessions.
 */
export function buildClosedChatSessionsQuery(firestore, companyId) {
  const tenantCompanyId = resolveTenantCompanyId(companyId);
  if (!tenantCompanyId) {
    throw new Error('buildClosedChatSessionsQuery: companyId is required');
  }

  return query(
    collection(firestore, CHAT_SESSIONS_COLLECTION),
    where('companyId', '==', tenantCompanyId),
    where('status', 'in', CLOSED_CHAT_STATUSES),
    orderBy('updatedAt', 'desc'),
  );
}

/** @deprecated Use isActiveChatStatus */
export function isOpenChatStatus(status) {
  return isActiveChatStatus(status);
}

/**
 * Tenant-scoped query for messages in a chat session.
 */
export function buildChatMessagesQuery(firestore, companyId, sessionId) {
  const tenantCompanyId = resolveTenantCompanyId(companyId);
  const chatId = typeof sessionId === 'string' ? sessionId.trim() : '';
  if (!tenantCompanyId || !chatId) {
    throw new Error('buildChatMessagesQuery: companyId and sessionId are required');
  }

  return query(
    collection(firestore, CHAT_SESSIONS_COLLECTION, chatId, 'messages'),
    where('companyId', '==', tenantCompanyId),
    orderBy('timestamp', 'asc'),
  );
}

/**
 * Normalize a Firestore message document for chat UIs.
 */
export function normalizeChatMessageDoc(docSnap, opts = {}) {
  const data = docSnap.data();
  const rawSender =
    typeof data.sender === 'string'
      ? data.sender
      : typeof data.senderType === 'string'
        ? data.senderType
        : 'visitor';
  const senderType = rawSender === 'agent' ? 'agent' : 'visitor';
  const isAgent = senderType === 'agent';
  const body =
    typeof data.text === 'string' && data.text.trim()
      ? data.text
      : typeof data.message === 'string'
        ? data.message
        : '';
  const visitorName =
    typeof opts.visitorDisplayName === 'string' && opts.visitorDisplayName.trim()
      ? opts.visitorDisplayName.trim()
      : typeof data.visitorName === 'string' && data.visitorName.trim()
        ? data.visitorName.trim()
        : 'You';

  return {
    id: docSnap.id,
    message: body,
    text: body,
    senderType,
    sender: isAgent ? 'agent' : 'visitor',
    senderId: data.senderId || data.agentUid || null,
    senderLabel: data.isInternal === true
      ? 'Internal note'
      : isAgent
        ? 'Support'
        : visitorName,
    isInternal: data.isInternal === true,
    timestamp: data.timestamp || data.createdAt || null,
  };
}
